<template>
  <div class="cav-root">
    <b-card>
      <div class="cav-header">
        <div class="cav-header__icon"><i class="fa-solid fa-timeline"></i></div>
        <div class="cav-header__text">
          <h4 class="cav-header__title">{{ $t('clientActivity.title') }}</h4>
          <p class="cav-header__subtitle">{{ $t('clientActivity.pickHint') }}</p>
        </div>

        <div class="cav-header__picker">
          <!-- selected client chip -->
          <div v-if="selectedClient" class="cav-selected">
            <b-avatar size="sm" variant="light-primary" :text="acronym(selectedClient)" class="mr-50"/>
            <span class="cav-selected__name">{{ selectedClient.firstName }} {{ selectedClient.lastName }}</span>
            <span class="cav-selected__meta">#{{ selectedClient.id }}</span>
            <button class="cav-selected__clear" @click="clearClient" v-b-tooltip :title="$t('operations.cancel')">
              <i class="fa-solid fa-xmark"></i>
            </button>
          </div>

          <!-- free-text search -->
          <div v-else class="cav-search">
            <i class="fa-solid fa-magnifying-glass cav-search__icon"></i>
            <b-form-input v-model="search" class="cav-search__input" autocomplete="off"
                          :placeholder="$t('clientActivity.searchPlaceholder')"
                          @input="onSearchInput" @focus="showResults = true" @blur="onBlur"/>
            <b-spinner v-if="searching" small class="cav-search__spinner"/>

            <div v-if="showResults && search && (results.length || !searching)" class="cav-search__results">
              <button v-for="client in results" :key="client.id" class="cav-search__row"
                      @mousedown.prevent="selectClient(client)">
                <b-avatar size="sm" variant="light-primary" :text="acronym(client)"/>
                <span class="cav-search__name">{{ client.firstName }} {{ client.lastName }}</span>
                <span class="cav-search__meta">{{ client.email }}</span>
                <span class="cav-search__meta">{{ getBusinessUnitNameById(client.businessUnitId) }}</span>
                <span class="cav-search__id">#{{ client.id }}</span>
              </button>
              <div v-if="!results.length && !searching" class="cav-search__none">
                {{ $t('clientActivity.noResults') }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-card>

    <b-card v-if="selectedClient">
      <client-activity-timeline :client-id="selectedClient.id"/>
    </b-card>

    <b-card v-else>
      <div class="cav-empty">
        <i class="fa-solid fa-user-clock cav-empty__icon"></i>
        <div class="cav-empty__title">{{ $t('clientActivity.selectClient') }}</div>
        <div class="cav-empty__hint">{{ $t('clientActivity.searchPlaceholder') }}</div>
      </div>
    </b-card>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { Debounce } from '@/model/Utils';
import ClientActivityTimeline from '@/components/partial/clients/ClientActivityTimeline.vue';

export default {
  name: 'ClientActivityView',
  components: { ClientActivityTimeline },
  data: () => ({
    search: '',
    results: [],
    searching: false,
    showResults: false,
    selectedClient: null,
    searchDebounce: Debounce(() => {}, 400),
  }),
  computed: {
    ...mapGetters('data', ['getBusinessUnitNameById']),
  },
  created() {
    this.searchDebounce = Debounce(this.runSearch, 400);
  },
  methods: {
    ...mapActions('clients', ['getAllClients']),
    onSearchInput() {
      if (!this.search) {
        this.results = [];
        return;
      }
      this.searching = true;
      this.showResults = true;
      this.searchDebounce();
    },
    async runSearch() {
      const text = this.search;
      if (!text) {
        this.results = [];
        this.searching = false;
        return;
      }
      try {
        // Backend free-text search covers first/last name, email, phone,
        // mobile, username, account number and client id.
        const response = await this.getAllClients({
          filter: { search: text },
          pagination: { page: 1, size: 8 },
        });
        // Ignore stale responses from earlier keystrokes.
        if (text === this.search) {
          this.results = response.data || [];
        }
      } finally {
        this.searching = false;
      }
    },
    selectClient(client) {
      this.selectedClient = client;
      this.showResults = false;
      this.search = '';
      this.results = [];
    },
    clearClient() {
      this.selectedClient = null;
      this.search = '';
      this.results = [];
    },
    onBlur() {
      // Delay so a click on a result still lands (mousedown is prevented, but
      // keep it safe for touch).
      setTimeout(() => { this.showResults = false; }, 200);
    },
    acronym(client) {
      const first = (client.firstName || ' ')[0] || '';
      const last = (client.lastName || ' ')[0] || '';
      return (first + last).toUpperCase().trim() || '?';
    },
  },
};
</script>

<style scoped lang="scss">
.cav-header {
  display: flex; align-items: center; gap: 1rem; flex-wrap: wrap;

  &__icon {
    width: 52px; height: 52px; border-radius: 14px; flex-shrink: 0;
    display: inline-flex; align-items: center; justify-content: center;
    font-size: 1.35rem; color: #fff;
    background: linear-gradient(135deg, #7367f0 0%, #9e95f5 100%);
    box-shadow: 0 10px 22px -12px rgba(115, 103, 240, 0.7);
  }
  &__text { min-width: 0; }
  &__title { margin-bottom: 0.1rem; font-weight: 800; }
  &__subtitle { margin-bottom: 0; font-size: 0.85rem; color: #9a9dad; }
  &__picker { margin-left: auto; min-width: 380px; position: relative; }
}

.cav-search {
  position: relative;

  &__icon {
    position: absolute; left: 0.8rem; top: 50%; transform: translateY(-50%);
    color: #9a9dad; pointer-events: none; z-index: 1;
  }
  &__input { padding-left: 2.2rem; border-radius: 10px; }
  &__spinner {
    position: absolute; right: 0.8rem; top: 50%; transform: translateY(-50%);
    color: #7367f0;
  }

  &__results {
    position: absolute; top: calc(100% + 6px); left: 0; right: 0; z-index: 20;
    background: #fff; border-radius: 12px; overflow: hidden;
    border: 1px solid rgba(110, 117, 159, 0.18);
    box-shadow: 0 14px 30px -12px rgba(20, 24, 50, 0.35);
  }

  &__row {
    display: flex; align-items: center; gap: 0.6rem; width: 100%;
    padding: 0.55rem 0.8rem; border: none; background: transparent;
    text-align: left; cursor: pointer;

    &:hover { background: rgba(115, 103, 240, 0.08); }
    + .cav-search__row { border-top: 1px solid rgba(110, 117, 159, 0.08); }
  }

  &__name { font-weight: 700; color: #5e5873; white-space: nowrap; }
  &__meta {
    font-size: 0.75rem; color: #9a9dad; min-width: 0;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  &__id {
    margin-left: auto; font-size: 0.72rem; font-weight: 700; color: #7367f0;
    white-space: nowrap;
  }
  &__none { padding: 0.7rem 0.9rem; font-size: 0.8rem; color: #9a9dad; }
}

.cav-selected {
  display: flex; align-items: center;
  padding: 0.4rem 0.6rem; border-radius: 10px;
  background: rgba(115, 103, 240, 0.08);
  border: 1px solid rgba(115, 103, 240, 0.3);

  &__name { font-weight: 700; color: #5e5873; }
  &__meta { margin-left: 0.5rem; font-size: 0.75rem; color: #7367f0; font-weight: 700; }
  &__clear {
    margin-left: auto; border: none; background: transparent; cursor: pointer;
    color: #9a9dad; padding: 0.2rem 0.4rem; border-radius: 6px;

    &:hover { color: #ea5455; background: rgba(234, 84, 85, 0.1); }
  }
}

.cav-empty {
  text-align: center; color: #9a9dad; padding: 3rem 1rem;

  &__icon { font-size: 2.4rem; opacity: 0.45; }
  &__title { font-weight: 700; margin-top: 0.7rem; font-size: 1rem; color: #5e5873; }
  &__hint { font-size: 0.82rem; margin-top: 0.2rem; }
}

body.dark-layout {
  .cav-search__results { background: #283046; border-color: rgba(255, 255, 255, 0.08); }
  .cav-search__name, .cav-selected__name { color: #d0d2d6; }
}

@media (max-width: 767px) {
  .cav-header__picker { margin-left: 0; width: 100%; min-width: 0; }
}
</style>
